import classNames from 'classnames';
import Immutable from 'immutable';
import React from 'react';

import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as SampleConstants from 'constants/SampleConstants';
import ManagedColumnsSimpleTableEditable from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {TableData, TableHeaderControlsArgs} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import DownloadAsCSVButton from 'notebook/tables/export/DownloadAsCSVButton';
import {getBooleanCellValue} from 'notebook/tables/typed-values/DataTypes';
import {getStore} from 'redux-stores/Store';
import {FulfillmentStatus} from 'requests/fulfillments/FulfillmentConstants';
import {bulkRemoveFulfillments, updateFulfillment} from 'requests/fulfillments/FulfillmentReduxActions';
import {requestSchemaToFulfillmentType} from 'requests/fulfillments/FulfillmentType';
import {HydratedFulfillmentWithPreparedSampleGroup} from 'requests/fulfillments/HydratedFulfillmentConstants';
import {restStorableSummariesByIdToGraphQL} from 'samples/storables/graphql/translateStorable';
import {withLoadingScreen} from 'util/Promises';

import createRequestFulfillmentsTable, {getColumnDefinitions} from './createRequestFulfillmentsTable';

function getStatusColumnIndex(columnDefinitions: Array<ColumnDefinition | null>): number {
  let statusColumnIndex = null;
  for (const [i, columnDefinition] of columnDefinitions.entries()) {
    if (columnDefinition != null && columnDefinition.type === TableColumnType.BOOLEAN) {
      assert(statusColumnIndex == null, 'Expected exactly one checkbox column');
      statusColumnIndex = i;
    }
  }
  assert(statusColumnIndex != null, 'Expected exactly one checkbox column');
  return statusColumnIndex;
}

export interface RequestFulfillmentSpreadsheetProps {
  locked: boolean;
  entryId: string;
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>;
  containersById: {[containerId: string]: SampleConstants.Container | null};
  ancestralStorablesById: {[storableId: string]: Array<SampleConstants.StorableSummary>};
  viewer?: boolean;
}

interface State {
  computedTable: Array<Array<EvaluatedCell>> | null;
}
export class RequestFulfillmentSpreadsheet extends React.Component<
  RequestFulfillmentSpreadsheetProps,
  State
> {
  override state: State = {
    computedTable: null,
  };

  _handleComputedTableChanged = (computedTable: Array<Array<EvaluatedCell>>): void => {
    this.setState({computedTable});
  };

  _getRemovedRowIds(table: TableData): Array<string> {
    const {hydratedFulfillments} = this.props;
    const originalRowIds = Immutable.Set(
      hydratedFulfillments.map((hydratedFulfillment) => hydratedFulfillment.fulfillment.id)
    );
    return originalRowIds.subtract(table.rowData!.map((rowData) => rowData.fulfillmentId!)).toArray();
  }

  _handleRowsRemoved(table: TableData): void {
    const removedRowIds = this._getRemovedRowIds(table);
    if (removedRowIds.length > 0) {
      getStore().dispatch((dispatch) =>
        withLoadingScreen(bulkRemoveFulfillments(removedRowIds)(dispatch), 'Deleting...')
      );
    }
  }

  _handleFulfillmentUpdated({
    hydratedFulfillment,
    statusAfterReview,
  }: {
    hydratedFulfillment: HydratedFulfillmentWithPreparedSampleGroup;
    statusAfterReview: FulfillmentStatus;
  }): void {
    getStore().dispatch(
      updateFulfillment(hydratedFulfillment.fulfillment.id, {
        statusAfterReview,
      })
    );
  }

  _handleRowsEdited = (table: TableData): void => {
    const {hydratedFulfillments, containersById, ancestralStorablesById, locked} = this.props;
    const columnDefinitions = getColumnDefinitions({
      hydratedFulfillments,
      containersById,
      ancestralStorablesById: restStorableSummariesByIdToGraphQL(ancestralStorablesById),
      locked,
    });

    const statusColumnIndex = getStatusColumnIndex(columnDefinitions);
    for (const [rowIndex, tableRow] of table.table.entries()) {
      const newStatusAfterReview = getBooleanCellValue(tableRow[statusColumnIndex])
        ? FulfillmentStatus.FAILED
        : FulfillmentStatus.COMPLETED;
      const hydratedFulfillment = hydratedFulfillments[rowIndex];
      // Only send a server request if the status actually changed
      if (newStatusAfterReview !== hydratedFulfillment.fulfillment.statusAfterReview) {
        this._handleFulfillmentUpdated({hydratedFulfillment, statusAfterReview: newStatusAfterReview});
      }
    }
  };

  _handleUpdate = (index: number, {table}: {table: TableData}): void => {
    this._handleRowsRemoved(table);
    this._handleRowsEdited(table);
  };

  override render(): JSX.Element {
    const {hydratedFulfillments} = this.props;
    assert(hydratedFulfillments.length > 0, 'Expected at least 1 fulfillment');

    const fulfillmentTypes = hydratedFulfillments.map((hydratedFulfillment) =>
      requestSchemaToFulfillmentType(hydratedFulfillment.requestSchema)
    );
    // Assert all fulfillments have same type
    const fulfillmentType = fulfillmentTypes[0];
    for (const [i, otherFulfillmentType] of fulfillmentTypes.entries()) {
      assert(otherFulfillmentType === fulfillmentType, 'Expected all fulfillments to be the same type', {
        fulfillmentType,
        schemaWithDifferentFulfillmentType: hydratedFulfillments[i].requestSchema,
      });
    }

    const {widths, ...table} = createRequestFulfillmentsTable({
      hydratedFulfillments: this.props.hydratedFulfillments,
      containersById: this.props.containersById,
      ancestralStorablesById: restStorableSummariesByIdToGraphQL(this.props.ancestralStorablesById),
      locked: this.props.locked,
      // isFinalized must be false so we can remove rows from the TableEditable
      isFinalized: false,
    });

    const renderHeaderControls = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
      if (isCollapsed) {
        return null;
      }
      return (
        <DownloadAsCSVButton
          tableLabel="Inputs"
          disabled={hydratedFulfillments.length === 0}
          analyticsEvent={ELN_ANALYTICS_ACTIONS.DOWNLOADED_REQUEST_FULFILLMENTS_TABLE_AS_CSV}
          table={table.table}
          columnLabels={table.columnLabels as Array<string>}
          computedTable={this.state.computedTable}
        />
      );
    };

    return (
      <div className={classNames('mediocre', {'modal-mediocre-tableWrapper': !this.props.viewer})}>
        <ManagedColumnsSimpleTableEditable
          initialColumnWidths={widths}
          tableWithoutColumnWidths={table}
          {...standaloneProps}
          tableLabel="Inputs"
          icon="input"
          cid={`request-fulfillment-table-${this.props.entryId}`}
          locked={false}
          viewer={this.props.viewer}
          hideFormulaBox={true}
          enableFormulas={false}
          enableFillDown={true}
          hasPlaceholderRows={false}
          headerControls={renderHeaderControls}
          isCollapsible={true}
          onComputedTableChanged={this._handleComputedTableChanged}
          storeState={() => {}}
          restoreState={() => {}}
          isLeftAlignColumnHeader={true}
          updateHandler={this._handleUpdate}
          dataTestAttrs={{
            component: 'RequestFulfillmentSpreadsheet',
            element: 'ManagedColumnsSimpleTableEditable',
          }}
        />
      </div>
    );
  }
}

export default RequestFulfillmentSpreadsheet;
